import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import { Geolocation } from '@awesome-cordova-plugins/geolocation/ngx';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { formatNumber } from '../utils/utilityFunction';
import {
    coordinateDecimalDigit,
    graphicLayerId,
    measurementDecimalDigit,
    measurementDrawMode,
    measurementUnit,
    visiblePropertyToc,
} from '../constants/map';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import * as identify from '@arcgis/core/rest/identify';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import { API_URL_MAP } from '../../environments/environment';
import Basemap from '@arcgis/core/Basemap';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';

export const zoomIn = (arcgis: ArcGisType) => {
    arcgis.mapView.goTo(
        {
            zoom: ++arcgis.mapView.zoom,
        },
        {
            // duration: 2000, // Duration of animation 2 seconds
        }
    );
};

export const zoomOut = (arcgis: ArcGisType) => {
    arcgis.mapView.goTo(
        {
            zoom: --arcgis.mapView.zoom,
        },
        {
            // duration: 2000,
        }
    );
};

export const pan = (
    arcgis: ArcGisType,
    params: { longitude: number; latitude: number },
    opt?: any
) => {
    arcgis.mapView.goTo({
        center: [params.longitude, params.latitude],
        ...opt,
    });
};

export const getCurrentLocation = async (geolocation: Geolocation) => {
    const { coords } = await geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
    });
    const { longitude, latitude } = coords;
    return { longitude, latitude };
};

export const setBasemap = (arcgis: ArcGisType, param: any) => {
    if (param.basemapUrl) {
        let basemap = new Basemap({
            id: param.basemapName,
            title: param.labelName,
            baseLayers: [
                new VectorTileLayer({
                    id: param.basemapName,
                    title: param.labelName,
                    url: param.basemapUrl,
                }),
            ],
        });

        arcgis.mapView.map.basemap = basemap as any;
    } else {
        arcgis.mapView.map.basemap = param.basemapName as any;
    }
};

export const getLayerById = (mapView: __esri.MapView, layerId) => {
    return mapView.map.findLayerById(layerId);
};

export const addLayerById = (arcgis: ArcGisType, layerId) => {
    const layer = new GraphicsLayer({ id: layerId });
    arcgis.mapView.map.layers.add(layer);
};

export const clearLayerById = (arcgis: ArcGisType, layerId) => {
    const layer = arcgis.mapView.map.findLayerById(layerId) as GraphicsLayer;

    if (layer) {
        layer.removeAll();
    }
};

export const addLayer = (arcgis: ArcGisType, layer) => {
    arcgis.mapView.map.layers.add(layer);
};

export const addGraphicsLayerById = (arcgis: ArcGisType, layerId) => {
    const layer = new GraphicsLayer({ id: layerId });
    addLayer(arcgis, layer);
};

export const addFeatureLayerByUrl = (
    arcgis: ArcGisType,
    param: { id: string; url: string }
) => {
    const layer = new FeatureLayer({ url: param.url, id: param.id });
    addLayer(arcgis, layer);
};

export const setVisibleFeatureLayer = (featureLayer, isVisibility: boolean) => {
    featureLayer.visible = isVisibility;
};

export const setVisibleTOC = (
    arcgis: ArcGisType,
    param: { id: string; isVisibility: boolean }
) => {
    const layer = arcgis.mapView.map.findLayerById(
        'basemaptoc'
    ) as __esri.MapImageLayer;
    layer.sublayers.find((itm) => itm.id == parseInt(param.id)).visible =
        param.isVisibility;
};

export const setVisibleLayerByID = (
    arcgis: ArcGisType,
    param: { id: string; isVisibility: boolean }
) => {
    const layer = getLayerById(arcgis.mapView, param.id);
    if (layer) {
        layer.visible = param.isVisibility;
    }
};

export const toggleVisibleLayerById = (arcgis: ArcGisType, layerId) => {
    const currentVisible = getLayerById(arcgis.mapView, layerId).visible;
    getLayerById(arcgis.mapView, layerId).visible = !currentVisible;
};

export const createMeasurementDraw = (arcgis: ArcGisType, layerId) => {
    const measurement = new SketchViewModel({
        view: arcgis.mapView,
        layer: getLayerById(arcgis.mapView, layerId),
        updateOnGraphicClick: false,
        defaultUpdateOptions: {
            toggleToolOnClick: false,
            multipleSelectionEnabled: false,
            enableScaling: false,
            enableRotation: false,
        },
        pointSymbol: {
            type: 'simple-marker',
            color: [49, 138, 241], // blue primary
            size: 10,
            outline: {
                color: [255, 255, 255, 0],
            },
        },
        polylineSymbol: {
            type: 'simple-line',
            color: [49, 138, 241], // blue primary
            width: 4,
        },
        polygonSymbol: {
            type: 'simple-fill',
            color: 'rgba(49, 138, 241, 0.8)', // blue primary opacity
            style: 'solid',
            outline: {
                color: 'white',
                width: 1,
            },
        },
    });
    return measurement;
};

export const drawMeasurement = (arcgis: ArcGisType, drawMode: string) => {
    switch (drawMode) {
        case measurementDrawMode.point:
            arcgis.drawerMeasurement?.create('point');
            break;
        case measurementDrawMode.distance:
            arcgis.drawerMeasurement?.create('polyline', {
                mode: 'click',
            });
            break;
        case measurementDrawMode.area:
            arcgis.drawerMeasurement?.create('polygon', {
                mode: 'click',
            });
            break;
        default:
            break;
    }
};

export const calculateDistance = (geometry: any, unit: __esri.LinearUnits) => {
    let result = (0).toFixed(measurementDecimalDigit);
    if (unit === measurementUnit.distance[4].nameEN) {
        // inch (นิ้ว)
        const feet = geometryEngine.geodesicLength(
            geometry,
            measurementUnit.distance[3].nameEN as __esri.LinearUnits
        );
        result = (feet * 12).toFixed(measurementDecimalDigit);
    } else {
        result = geometryEngine
            .geodesicLength(geometry, unit)
            .toFixed(measurementDecimalDigit);
    }

    return parseFloat(result);
};

export const calculateArea = (geometry: any, unit: __esri.ArealUnits) => {
    const result = geometryEngine
        .geodesicArea(geometry, unit)
        .toFixed(measurementDecimalDigit);

    return parseFloat(result);
};

export const calculateRai = (geometry: any) => {
    let squareMeter = calculateArea(
        geometry,
        measurementUnit.area[1].nameEN as __esri.ArealUnits
    );

    const result = squareMeter / 1600; // 1 ไร่ = 1600 ตารางเมตร : ไร่ = ตารางเมตร/1600

    return parseFloat(result.toFixed(measurementDecimalDigit));
};

export const calculateNgan = (geometry: any) => {
    let squareMeter = calculateArea(
        geometry,
        measurementUnit.area[1].nameEN as __esri.ArealUnits
    );

    const result = squareMeter / 400; // 1 งาน = 400 ตารางเมตร: งาน = ตารางเมตร/400

    return parseFloat(result.toFixed(measurementDecimalDigit));
};

export const calculateWa = (geometry: any) => {
    let squareMeter = calculateArea(
        geometry,
        measurementUnit.area[1].nameEN as __esri.ArealUnits
    );

    const result = squareMeter / 4; // 100 ตารางวา  = 400 ตารางเมตร : ตารางวา = ตารางเมตร/4

    return parseFloat(result.toFixed(measurementDecimalDigit));
};

export const calculateRaiNganWa = (geometry: any) => {
    let squareMeter = calculateArea(
        geometry,
        measurementUnit.area[1].nameEN as __esri.ArealUnits
    );

    const rai = squareMeter / 1600; // 1 ไร่ = 1600 ตารางเมตร : ไร่ = ตารางเมตร/1600
    squareMeter %= 1600; //เอาตารางเมตรที่เหลือ
    const ngan = squareMeter / 400; // 1 งาน = 400 ตารางเมตร: งาน = ตารางเมตร/400
    squareMeter %= 400; //เอาตารางเมตรที่เหลือ
    const squareWa = squareMeter / 4; // 100 ตารางวา  = 400 ตารางเมตร : ตารางวา = ตารางเมตร/4

    return {
        rai: parseFloat(rai.toFixed(measurementDecimalDigit)),
        ngan: parseFloat(ngan.toFixed(measurementDecimalDigit)),
        squareWa: parseFloat(squareWa.toFixed(measurementDecimalDigit)),
    };
};

export const calculateMeasurement = (
    drawMode: string,
    unit: string,
    geometry
) => {
    let measurementResult = '';
    if (drawMode === measurementDrawMode.area) {
        if (
            unit === measurementUnit.area[2].nameEN ||
            unit === measurementUnit.area[3].nameEN ||
            unit === measurementUnit.area[4].nameEN ||
            unit === measurementUnit.area[5].nameEN
        ) {
            if (unit === measurementUnit.area[2].nameEN) {
                const result = calculateRai(geometry);
                measurementResult = `${formatNumber(result)} ${
                    measurementUnit.area[2].unitTH
                }`;
            } else if (unit === measurementUnit.area[3].nameEN) {
                const result = calculateNgan(geometry);
                measurementResult = `${formatNumber(result)} ${
                    measurementUnit.area[3].unitTH
                }`;
            } else if (unit === measurementUnit.area[4].nameEN) {
                const result = calculateWa(geometry);
                measurementResult = `${formatNumber(result)} ${
                    measurementUnit.area[4].unitTH
                }`;
            } else if (unit === measurementUnit.area[5].nameEN) {
                const { rai, ngan, squareWa } = calculateRaiNganWa(geometry);
                measurementResult = `${formatNumber(rai)} ${
                    measurementUnit.area[2].unitTH
                } ${formatNumber(ngan)} ${
                    measurementUnit.area[3].unitTH
                } ${formatNumber(squareWa)} ${measurementUnit.area[4].unitTH}`;
            }
        } else {
            const result = calculateArea(geometry, unit as __esri.ArealUnits);
            measurementResult = `${formatNumber(result)} ${
                measurementUnit.area.find((item) => item.nameEN === unit).unitTH
            }`;
        }
    } else if (drawMode === measurementDrawMode.distance) {
        const result = calculateDistance(geometry, unit as __esri.LinearUnits);
        measurementResult = `${formatNumber(result)} ${
            measurementUnit.distance.find((item) => item.nameEN === unit).unitTH
        }`;
    } else if (drawMode === measurementDrawMode.point) {
        const { latitude, longitude } = geometry;
        measurementResult = `${parseFloat(latitude).toFixed(
            coordinateDecimalDigit
        )}, ${parseFloat(longitude).toFixed(coordinateDecimalDigit)}`;
    }
    return measurementResult;
};

export const addPointGraphic = (
    arcgis: ArcGisType,
    param: __esri.PointProperties
) => {
    const markerSymbol = {
        type: 'simple-marker', // autocasts as new SimpleMarkerSymbol()
        color: [49, 138, 241],
        outline: {
            // autocasts as new SimpleLineSymbol()
            color: [255, 255, 255],
            width: 2,
        },
    };

    const layer = getLayerById(arcgis.mapView, 'checkTaxLayer');
    const pointGraphic = new Graphic({
        geometry: param,
        symbol: markerSymbol,
        layer: layer,
    });

    arcgis.mapView.graphics.add(pointGraphic);
};

export const addHighlightGraphic = (arcgis: ArcGisType, param: any) => {
    const layer = getLayerById(
        arcgis.mapView,
        graphicLayerId.highlightLayer
    ) as GraphicsLayer;

    if (param.type == 'polygon') {
        let symbol = {
            type: 'simple-fill', // autocasts as new SimpleFillSymbol()
            color: [51, 51, 204, 0.7],
            style: 'solid',
            outline: {
                // autocasts as new SimpleLineSymbol()
                color: 'white',
                width: 1,
            },
        };
        let polygonGraphic = new Graphic({
            geometry: param,
            symbol: symbol,
        });

        layer.add(polygonGraphic);
    } else if (param.type == 'point') {
        const simpleMarkerSymbol = {
            type: 'simple-marker',
            //style: 'square',
            size: '32px',
            color: [51, 51, 204, 0.7], // Orange
            outline: {
                color: [255, 255, 255], // White
                width: 1,
            },
        };
        let pointGraphic = new Graphic({
            geometry: param,
            symbol: simpleMarkerSymbol,
            layer: layer,
        });

        layer.add(pointGraphic);
    }
};

export const addPointHightlight = (arcgis: ArcGisType, param: any) => {
    const layer = getLayerById(
        arcgis.mapView,
        graphicLayerId.highlightLayer
    ) as GraphicsLayer;

    const simpleMarkerSymbol = {
        type: 'simple-marker',
        //style: 'square',
        size: '16px',
        color: [226, 119, 40], // Orange
        outline: {
            color: [255, 255, 255], // White
            width: 1,
        },
    };
    let pointGraphic = new Graphic({
        geometry: param,
        symbol: simpleMarkerSymbol,
        layer: layer,
    });
    layer.add(pointGraphic);
};

export const identifyMap = async (
    arcgis: ArcGisType,
    mapPoint: __esri.PointProperties
) => {
    const identifyParams = new IdentifyParameters({
        tolerance: 10,
        layerIds: visiblePropertyToc,
        layerOption: 'visible',

        width: arcgis.mapView.width,
        height: arcgis.mapView.height,
        mapExtent: arcgis.mapView.extent,
        geometry: mapPoint,
        returnGeometry: true,
    });

    const identifyUrl = API_URL_MAP;

    const { results } = await identify.identify(identifyUrl, identifyParams);

    return results;
};

export const setPolygon = (polygonJson) => {
    let polygon = new Polygon(polygonJson);
    let fillSymbol = {
        type: 'simple-fill', // autocasts as new SimpleFillSymbol()
        color: [227, 139, 79, 0.8],
        outline: {
            // autocasts as new SimpleLineSymbol()
            color: [255, 255, 255],
            width: 1,
        },
    };
};
